<script setup>
import timezones from '@/api/timezones'
import { displayTimeOffsetUtil } from '@/utils/time'
import { DateTime } from 'luxon'
import { Mask } from 'maska'
import { storeToRefs } from 'pinia'
import useEventDataStore from '../stores/eventDataStore'
import useFeatureFlagStore from '../stores/featureFlagStore'
import BaseUrl from '~/api/BaseUrl.js'
import ENDPOINTS from '~/api/endpoints/index.js'
import Headers from '~/api/Headers.js'
import  isEqual  from 'lodash/isEqual.js'

const mask = new Mask({ mask: '(###) ###-####' })

const router = useRouter()
const route = useRoute()
const { eventData, error: errorLoadingData } = storeToRefs(useEventDataStore())
const eventDataStore = useEventDataStore()

const eventDataUrl = `${BaseUrl.get(ENDPOINTS.EVENTS.GET_EVENT_PAGE_CONFIG)}${
  ENDPOINTS.EVENTS.GET_EVENT_PAGE_CONFIG
}?event_id=${route.params.id}`
const { data, error: apiError, execute } = await useFetch(
  eventDataUrl,
  { headers: Headers },
  { key: Date.now(), immediate: false }
)

await execute()

if(!eventData.value && data || !isEqual(data.value, eventData.value)){
  nextTick(() => {
    eventDataStore?.setEventData(data?.value ?? {}, apiError?.value ?? {})
    setTimeZone(data);
  });
}
const eventTimeZone = ref(null)

const setTimeZone = (eventData) => {
  if (eventData?.value?.event_timezone) {
    eventTimeZone.value = DateTime.fromISO(eventData.value?.event_start_date, { zone: eventData.value?.event_timezone }).toFormat("ZZZZ");
  }
}

watch(eventData, () => {
  setTimeZone(eventData);
}, {deep: true});

useHead({
  meta: [
    {
      property: 'og:title',
      content: data?.value?.event_title
    },
    { property: 'og:image', content: data?.value?.banner_image },
    { property: 'twitter:card', content: 'summary' }
  ]
})
const flagDataStore = useFeatureFlagStore()
const { flagData, error: errorLoadingFlagData } = storeToRefs(flagDataStore)

let apiEnvironment = "";

switch (true) {
  case window?.location.hostname.includes("v4app"):
    apiEnvironment = "production";
    break;
  case window?.location.hostname.includes("beta"):
    apiEnvironment = "beta";
    break;
  case window?.location.hostname.includes("qa"):
    apiEnvironment = "qa";
    break;
  case window?.location.hostname.includes("staging"):
    apiEnvironment = "staging";
    break;
  default:
    apiEnvironment = "localhost";
}


const featureFlagsUrl = computed(
  () =>
    `${BaseUrl.get(ENDPOINTS.META.SETTINGS)}${
      ENDPOINTS.META.SETTINGS
    }?id=${apiEnvironment}_frontend&event_id=${route.params.id}`
);

const { data: featureFlags, error: errorGettingFeatureFlags, execute: executeFlags } = await useFetch(
  featureFlagsUrl,
  {
    headers: Headers
  },
  {
    key: Date.now()
  }
)
await executeFlags()

if(!flagData?.value && featureFlags || !isEqual(featureFlags?.value, flagData?.value)) {
  nextTick(() => flagDataStore?.setFlagsData(featureFlags?.value ?? {}, errorGettingFeatureFlags?.value ?? {}))
}

onMounted(() => {
  document.addEventListener('wheel', function (event) {
    if (document.activeElement.type === 'number' && !window.disabledNumberScroll) {
      document.activeElement.blur()
      window.disabledNumberScroll = true
    }
  })
})

const handleRegistrationClick = () => {
  if (data.value?.registration_open) {
    router.push({
      name: 'attendance-id-ticket-selection',
      params: { id: route.params.id }
    })
  } else {
    router.push({
      name: 'attendance-id-registration-closed',
      params: { id: route.params.id }
    })
  }
}

const isSameDate = (date1, date2) => {
  const timezone = data.value?.event_timezone || 'UTC'
  const d1 = DateTime.fromISO(date1).setZone(timezone)
  const d2 = DateTime.fromISO(date2).setZone(timezone)
  return d1.hasSame(d2, 'day')
}
</script>

<template>
  <v-container
    class="pa-0"
    :style="{
      backgroundColor: data?.event_color || 'none',
      position: 'relative',
      margin: 0,
      maxWidth: '100vw',
      minHeight: '100%',
      overflow: 'auto'
    }"
    v-if="data !== undefined && data !== null && Object.keys(data)?.length > 0"
  >
    <v-row no-gutters style="height: 100%; width: 100%" justify="center">
      <v-col sm="12" :style="`background-color:#E5E7EB; position:relative`">
        <div
          v-if="data?.banner_image"
          class="backdrop"
          :style="{
            backgroundImage: `linear-gradient(rgba(211, 211, 211, 0.45), rgba(211, 211, 211, 0.45)),url(${data?.banner_image})`
          }"
        />
        <main
          class="w-100 d-flex flex-column pa-0 pa-sm-4 pa-lg-16"
          style="position: relative; top: 0; overflow: auto"
        >
          <div class="w-100">
            <img :src="data?.banner_image" style="height: 100%; width: 100%" />
          </div>

          <v-row
            class="mb-6"
            no-gutters
            style="background-color: var(--fundeasy-colors-generic-00)"
          >
            <v-col class="pa-4 pa-lg-12">
              <v-row class="mb-4 mb-lg-12" no-gutters>
                <v-col>
                  <v-row
                    no-gutters
                    class="text-center text-md-start justify-center justify-lg-start"
                    :style="{'justify-content':route.name === 'attendance-id-registration-closed' ||
                      route.name === 'attendance-id-confirmation-cartId' ||
                      route.name === 'attendance-id-express-checkin' ||
                      route.name === 'attendance-id-decline-invitation-invitationId' ? 'center':'' }"
                    :class="{
                      'fs-14':$vuetify.display.xs
                    }"
                  >
                    <img
                      class="mr-4"
                      style="width: 25px; height: 25px"
                      :src="
                        data?.account_logo ? data?.account_logo : '/assets/FE_icon.svg'
                      "
                      alt="Account Logo"
                    />
                    <p style="color: var(--fundeasy-colors-neutral-500)">
                      {{ data?.account_name }}
                    </p>
                  </v-row>
                  <v-row
                    no-gutters
                    class="justify-center justify-lg-start"
                    :style="{'justify-content':route.name === 'attendance-id-registration-closed' ||
                      route.name === 'attendance-id-confirmation-cartId' ||
                      route.name === 'attendance-id-express-checkin' ||
                      route.name === 'attendance-id-decline-invitation-invitationId' ? 'center':'' }"
                    :class="{
                      'fs-14':$vuetify.display.xs
                    }"
                  >
                    <h1 :class="{
                      'fs-36': $vuetify.display.mdAndUp,
                      'fs-28':$vuetify.display.sm,
                      'fs-24':$vuetify.display.xs
                    }">{{ data?.event_title }} </h1>
                  </v-row>
                  <v-row
                    no-gutters
                    :style="{'justify-content':route.name === 'attendance-id-registration-closed' ||
                      route.name === 'attendance-id-confirmation-cartId' ||
                      route.name === 'attendance-id-express-checkin' ||
                      route.name === 'attendance-id-decline-invitation-invitationId' ? 'center':'' }"
                    :class="{
                      'fs-14':$vuetify.display.xs
                    }"
                    class="fs-14 mb-4 mb-lg-0 justify-center justify-lg-start"
                  >
                    <v-icon
                      size="small"
                      variant="default"
                      icon="mdi-calendar-blank-outline"
                      class="mr-2"
                    ></v-icon>
                    <span class="mr-1">{{
                      DateTime.fromISO(data?.event_start_date, { zone: data?.event_timezone }).toFormat("cccc, LLLL d, yyyy")
                    }}</span>
                    <span
                      v-if="
                        !DateTime.fromISO(data?.event_start_date).startOf('day').equals(DateTime.fromISO(data?.event_end_date).startOf('day'))
                      "
                    >
                      -
                      {{ DateTime.fromISO(data?.event_end_date, { zone: data?.event_timezone }).toFormat("cccc, LLLL d, yyyy") }}
                    </span>
                    <v-icon
                      size="small"
                      variant="default"
                      icon="mdi-clock-time-eight-outline"
                      class="mr-2 ml-6"
                    ></v-icon>
                    <template v-if="data?.event_start_date">
                      <span class="mr-1">
                        {{
                          displayTimeOffsetUtil(
                            data?.event_start_date,
                            data?.event_timezone
                          )
                        }}
                      </span>
                      <span>
                        -
                        {{
                          displayTimeOffsetUtil(
                            data?.event_end_date,
                            data?.event_timezone
                          )
                        }}
                      </span>
                    </template>
                    <span class="ml-2">
                      {{ eventTimeZone }}
                    </span>
                    <v-icon
                      size="small"
                      variant="default"
                      icon="mdi-map-marker-outline"
                      class="mr-2 ml-6"
                    ></v-icon>
                    <span class="mr-1"
                      >{{ data?.location_details?.city }} ,
                      {{ data?.location_details?.state }}</span
                    >
                  </v-row>
                  <div v-if="$route.name === 'attendance-id-event'" class="d-flex d-lg-none justify-center">
                    <v-btn
                      @click="handleRegistrationClick"
                      class="text-transform-none"
                      variant="elevated"
                      :color="data?.value?.event_color ?? data?.event_color ?? 'info'"
                      style="color: var(--fundeasy-colors-generic-00); height: 48px; width: 240px"
                    >{{data?.registration_btn_label ? data?.registration_btn_label: 'Register For Event' }}</v-btn
                    >
                  </div>
                </v-col>
              </v-row>
              <div>
                <slot v-if="!errorLoadingData" />
              </div>
            </v-col>
            <!-- Master of Ceremony -->
            <v-col cols="12"  lg="4" v-if="$route.name === 'attendance-id-event' && !data.event_preferences?.disableParticipantsRegs">
              <aside class="pa-lg-12 pa-4">
                <v-row no-gutters class="mb-4" justify="center">
                  <v-btn
                    @click="handleRegistrationClick"
                    class="w-100 text-transform-none d-none d-lg-inline-block"
                    variant="elevated"
                    :color="data?.value?.event_color ?? data?.event_color ?? 'info'"
                    style="color: var(--fundeasy-colors-generic-00); height: 48px"
                    >{{!!data?.registration_btn_label ? data?.registration_btn_label : 'Register For Event' }}</v-btn
                  >
                </v-row>
                <v-divider class="mb-4"></v-divider>
                <v-row no-gutters>
                  <v-col style="border: 1px solid #cacaca" class="pa-3">
                    <div v-if="data?.promotional_information?.speaker_name">
                      <p
                        class="mb-3"
                        style="color: var(--fundeasy-colors-neutral-500); font-weight: bold"
                      >
                        Speaker
                      </p>
                      <v-divider class="mb-4"></v-divider>
                      <p class="mb-4">{{ data?.promotional_information?.speaker_name }}</p>
                    </div>
                    <div
                      class="mb-4"
                      v-html="data?.promotional_information?.speaker_description"
                    ></div>
                    <div v-if="data?.promotional_information?.master_of_ceremony_name">
                      <p
                        class="mb-3"
                        style="color: var(--fundeasy-colors-neutral-500); font-weight: bold"
                      >
                        Master of Ceremonies
                      </p>
                      <v-divider class="mb-4"></v-divider>
                      <p>{{ data?.promotional_information?.master_of_ceremony_name }}</p>
                    </div>
                  </v-col>
                </v-row>
              </aside>
            </v-col>

            <v-col style="width: fit-content; max-width: fit-content" v-if="$slots['main-aside']">
              <slot name="main-aside"></slot>
            </v-col>
          </v-row>

          <!-- footer  -->
          <v-row
            no-gutters
            style="background-color: var(--fundeasy-colors-generic-00)"
            class="pl-12 pr-12 pt-4 pb-4"
          >
            <v-col>
              <a
                v-if="data?.promotional_information?.org_website"
                :href="data?.promotional_information?.org_website"
                target="_blank"
                >{{ data?.promotional_information?.org_website }}</a
              >
              <p>
                For help, contact
                {{ data?.point_of_contact?.primary_event_contact_first_name }}
                {{ data?.point_of_contact?.primary_event_contact_last_name }} at +{{
                  data?.point_of_contact?.primary_event_contact_phone_code
                }}
                {{
                  mask.masked(
                    String(data?.point_of_contact?.primary_event_contact_phone_number)
                  )
                }}
                or
                <a :href="`mailto:${data?.point_of_contact?.primary_event_contact_email}`">{{
                  data?.point_of_contact?.primary_event_contact_email
                }}</a>
              </p>
            </v-col>
            <v-col sm="12" lg="4">
              <v-row no-gutters justify="end" align="center">
                <img
                  style="height: 40px; width: 161px"
                  src="/attendance_logo.png"
                  alt="Attendance logo"
                />
              </v-row>
            </v-col>
          </v-row>
        </main>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.backdrop {
  min-height: 400px;
  height: 400px;
  min-width: 100%;
  position: absolute;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  filter: blur(8px);
  -webkit-filter: blur(8px);
  z-index: 0;
  top: 0;
  left: 0;
}
.mb-4 img {
  width: 100%;
}
</style>
<style>
@import '@/AppVariables.css';
</style>
